import { useEffect, useState } from 'react'
import { Checkbox } from 'components/Checkbox'
import { Icon } from 'components/Icon'

import TextField from '@material-ui/core/TextField'
import Chip from '@material-ui/core/Chip'

import * as S from './styled'

export type InputAutocompleteProps = {
  defaultValue?: any
  error?: boolean
  fullWidth?: boolean
  helperText?: string
  padding?: number
  label?: string
  options: unknown[]
  optionLabel: string
  width?: number
  onSelected?: (_value: any) => any
  clearFilter?: () => void
  disableRemoveButton?: boolean
  allowRemoveAllItems?: boolean
}

export function InputAutocomplete({
  defaultValue = [],
  error = false,
  fullWidth = false,
  helperText = '',
  label = '',
  options,
  padding,
  optionLabel,
  width = 200,
  onSelected,
  clearFilter,
  disableRemoveButton = false,
  allowRemoveAllItems = true
}: InputAutocompleteProps) {
  const [value, setValue] = useState<any>(defaultValue)
  const [isExpanded, setIsExpanded] = useState(false)
  const [hasOverflow, setHasOverflow] = useState(false)

  function handleChange(
    newValue,
    componentName: 'remove-option' | 'clear' | 'select-option'
  ) {
    const executeHandle = () => {
      const setObj = new Set()
      const result = newValue.reduce((acc, item) => {
        if (!setObj.has(item[optionLabel])) {
          setObj.add(item[optionLabel])
          acc.push(item)
        }
        return acc
      }, [])
      setValue(result)
      !!onSelected && onSelected(result)
    }

    if (allowRemoveAllItems) executeHandle()
    if (
      (disableRemoveButton && newValue.length) ||
      (componentName === 'remove-option' && newValue.length) ||
      (!disableRemoveButton &&
        componentName === 'select-option' &&
        newValue.length)
    ) {
      executeHandle()
    }
  }

  function handleCheckDelete(val) {
    const newValue = value.filter(each => each[optionLabel] !== val)
    if ((newValue.length && !disableRemoveButton) || allowRemoveAllItems) {
      setValue(newValue)
      !!onSelected && onSelected(newValue)
    }
  }

  // Gerencia o estado dos chips
  useEffect(() => {
    if (defaultValue) {
      const newValue = defaultValue.map(each => {
        const formated = { ...each }
        return formated
      })
      setValue(newValue)
    }
  }, [defaultValue, optionLabel])

  // Lida com o clique fora do componente para fechar os chips expandidos
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (isExpanded && !target.closest('[aria-expanded="true"]')) {
        setIsExpanded(false)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [isExpanded])

  return (
    <S.InputAutocomplete
      multiple
      padding={padding}
      options={options}
      disableCloseOnSelect
      getOptionLabel={option => option[optionLabel]}
      onChange={(_event: any, newValue: unknown, nameComponent) =>
        handleChange(newValue, nameComponent)
      }
      value={value}
      getOptionSelected={(option, value) =>
        option[optionLabel] === value[optionLabel]
      }
      popupIcon={<Icon icon="input-chevron-down" width={14} height={14} />}
      closeIcon={
        <Icon icon="input-exit" width={12} height={12} onClick={clearFilter} />
      }
      renderOption={option => (
        <>
          <Checkbox
            label=""
            labelFor=""
            style={{ marginRight: 8 }}
            value={option[optionLabel]}
            isChecked={
              value && !!value.find(f => f[optionLabel] === option[optionLabel])
            }
          />
          {option[optionLabel]}
        </>
      )}
      style={{ width: !fullWidth ? width : '100%' }}
      renderInput={params => (
        <>
          <TextField
            {...params}
            variant="outlined"
            label={label}
            error={error}
            helperText={helperText}
            hidden
          />
          {error && helperText && (
            <S.AlertIcon>
              <Icon icon="itens-interface-alert" height={10} width={10} />
            </S.AlertIcon>
          )}
        </>
      )}
      renderTags={(value, getTagProps) => {
        const checkOverflow = ref => {
          if (ref) {
            // Só mostrar o indicador de overflow se realmente houver conteúdo escondido
            const hasHiddenContent = ref.scrollHeight > ref.clientHeight
            setHasOverflow(hasHiddenContent)
          } else {
            setHasOverflow(false)
          }
        }

        return (
          <div
            onMouseEnter={() => setIsExpanded(true)}
            onMouseLeave={() => setIsExpanded(false)}
            onFocus={() => setIsExpanded(true)}
            onBlur={() => setIsExpanded(false)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setIsExpanded(!isExpanded)
              }
              if (e.key === 'Escape') {
                setIsExpanded(false)
              }
            }}
            style={{ position: 'relative', width: '100%' }}
            role="region"
            aria-label="Lista de tags selecionadas"
            aria-expanded={isExpanded}
            tabIndex={0}
          >
            <S.ChipsContainer isExpanded={isExpanded} ref={checkOverflow}>
              {value.map((option, index) => (
                <Chip
                  key={index}
                  variant="outlined"
                  label={option[optionLabel]}
                  deleteIcon={
                    disableRemoveButton ? (
                      <S.EmptyRemoveButton />
                    ) : (
                      <Icon icon="input-exit" width={12} height={12} />
                    )
                  }
                  {...getTagProps({ index })}
                  onClick={() => handleCheckDelete(option[optionLabel])}
                />
              ))}
            </S.ChipsContainer>
            <S.ChipOverflowIndicator show={hasOverflow && !isExpanded} />
            <S.ChipsOverlay isExpanded={isExpanded} />
          </div>
        )
      }}
    />
  )
}
